import { ref, Ref } from 'vue';
import { getDonationStep, getDonationSteps } from '@/utils/donationStepUtils';
import { deleteRequest, ENDPOINTS } from '@/api/mainApi';
import { DonationStep } from '@/interfaces/donationStep';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { DonationType } from '@/classes/DonationType';
import store from '@/store';

/**
 * Composable version of donationStepperFlowMixin.
 *
 * Notes:
 * - Accepts optional refs so components can pass their current refs.
 * - If a consumer doesn't pass availableStepsRef it will be created inside the composable.
 */
export function useStepperFlow(options?: {
  availableStepsRef?: Ref<DonationStep[]>;
  donationRef?: Ref<Donation>;
  isDigitalPaymentRef?: Ref<boolean>;
  defaultDonationGuideVersion?: DonationGuideVersion;
}) {
  const availableStepsRef =
    options?.availableStepsRef ??
    ref(getDonationSteps(options?.defaultDonationGuideVersion ?? DonationGuideVersion.V1));
  const donationRef = options?.donationRef;
  const isDigitalPaymentRef = options?.isDigitalPaymentRef;
  const defaultDonationGuideVersion =
    options?.defaultDonationGuideVersion ?? DonationGuideVersion.V1;

  function getDonationStepFlowVersion(): DonationGuideVersion {
    return store.state.donationGuideVersion || defaultDonationGuideVersion;
  }

  function getDonationStepLocal(stepNumber: number) {
    return getDonationStep(stepNumber, getDonationStepFlowVersion());
  }

  function getDonationGuideVersionFromRequestSession(
    requestSession: RequestSession
  ): DonationGuideVersion {
    return requestSession?.donationGuideVersion || defaultDonationGuideVersion;
  }

  function setDonationGuideVersion(donationGuideVersion: DonationGuideVersion) {
    store.commit('setDonationGuideVersion', donationGuideVersion);
  }

  function setDonationGuideVersionFromRequestSession(requestSession: RequestSession) {
    const version: DonationGuideVersion = getDonationGuideVersionFromRequestSession(requestSession);
    setDonationGuideVersion(version);
  }

  function getDonationStepsLocal(): DonationStep[] {
    return [...getDonationSteps(getDonationStepFlowVersion())];
  }

  function scrollToTopInstant() {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function setStep(step: number) {
    store.commit('updateCurrentStep', step);
  }

  function previousStep(currentStep: DonationStep | number | null | undefined) {
    const currentStepNumber = typeof currentStep === 'number' ? currentStep : currentStep?.step;
    if (typeof currentStepNumber !== 'number') return;

    const previousSteps = (availableStepsRef.value || [])
      .map((s) => s.step)
      .filter((s) => s < currentStepNumber)
      .sort((a, b) => b - a);

    if (previousSteps.length > 0) {
      setStep(previousSteps[0]);
      // safe attempt to scroll and nextTick-like behaviour is left to callers
      scrollToTopInstant();
    }
  }

  function nextStep(currentStep: DonationStep | number | null | undefined) {
    const currentStepNumber = typeof currentStep === 'number' ? currentStep : currentStep?.step;
    if (typeof currentStepNumber !== 'number') return;

    const nextSteps = (availableStepsRef.value || [])
      .map((s) => s.step)
      .filter((s) => s > currentStepNumber)
      .sort((a, b) => a - b);

    if (nextSteps.length > 0) {
      scrollToTopInstant();
      setStep(nextSteps[0]);
    }
  }

  function removeStep(stepToRemove: number) {
    const index = availableStepsRef.value.findIndex((v) => v.step === stepToRemove);
    if (index >= 0) availableStepsRef.value.splice(index, 1);
  }

  function addStep(stepToAdd: number) {
    let guideStep = availableStepsRef.value.find((v) => v.step === stepToAdd);
    if (!guideStep) {
      guideStep = getDonationStepLocal(stepToAdd);
      if (guideStep) {
        availableStepsRef.value.push(guideStep);
        availableStepsRef.value.sort((a, b) => a.step - b.step);
      }
    }
  }

  async function reset() {
    // best-effort behaviour matching previous mixin
    const donation = donationRef?.value;
    const isDigital =
      isDigitalPaymentRef?.value ??
      !!(donation && donation.paymentSpecification && donation.paymentSpecification.paymentMethod);
    if (!donation) return;

    if (isDigital) {
      await deleteRequest(`${ENDPOINTS.PAYMENTS}/${ENDPOINTS.TRANSACTIONS}`, {
        donationId: donation.id,
      });
    } else {
      await deleteRequest(`${ENDPOINTS.INVOICE}/${ENDPOINTS.FINALIZE}`, {
        donationId: donation.id,
      });
      const resetDonation = { ...donation, id: null };
      store.commit('updateDonation', resetDonation);
    }

    // set second step where present
    if (availableStepsRef.value.length > 1) {
      setStep(availableStepsRef.value[1].step);
    }
  }

  function resetSteps() {
    availableStepsRef.value = getDonationStepsLocal();
  }

  function handleDonationTypeVersionSwitch(
    donationType: DonationType,
    donationGuideVersion: DonationGuideVersion
  ) {
    // update store version
    store.commit('setDonationGuideVersion', donationGuideVersion);
    // safe nextTick behavior not included here — components should call getInitialSteps and setStep if they need to
    const steps = getDonationStepsLocal();
    const newStep: number =
      donationType.type === 'CONTRIBUTION'
        ? (steps.find((s) => s.title === 'DONOR')?.step ?? steps[0].step)
        : (steps.find((s) => s.title === 'INFORMATION')?.step ?? steps[0].step);
    setStep(newStep);
  }

  // convenience / light defaults
  function getInitialSteps() {
    return getDonationStepsLocal();
  }

  return {
    availableStepsRef,
    getDonationStep: getDonationStepLocal,
    getInitialSteps,
    previousStep,
    nextStep,
    removeStep,
    addStep,
    setStep,
    scrollToTopInstant,
    reset,
    getDonationStepFlowVersion,
    resetSteps,
    handleDonationTypeVersionSwitch,
    // expose these request-session / store helpers used in a few consumers
    getDonationGuideVersionFromRequestSession,
    setDonationGuideVersion,
    setDonationGuideVersionFromRequestSession,
  } as const;
}

export default useStepperFlow;
